/**
 * `PackageInspectorShow` — the one shared Inspector-grammar show page for every
 * package, typed or not (design-system §5, "no bespoke per-entity screens").
 *
 * Used directly by the unified `packages` resource and by the type-scoped
 * facades (`bets` / `frames` / `principles`). It renders the canonical Header /
 * Overview / Body / Relations / Audit sections from one `Package` record plus
 * its `package_links`:
 *
 *   - **Header**   — name · id · status · the named primary actions
 *                    (compile · advance maturity · facade-specific extras) and
 *                    an overflow `…` menu (Edit / Delete).
 *   - **Overview** — a `FieldGrid` of the package columns; every `*_id` is a
 *                    `RefLink`; skill-/contour-typed columns appear only for
 *                    their type.
 *   - **Body**     — `body_markdown` (rendered) when present.
 *   - **Relations**— outgoing + incoming `package_links`, each a `RefLink`.
 *   - **Audit**    — the generic `/api/packages/:id/timeline` (teaser until it
 *                    ships).
 *
 * **Data vs route split.** `bets` / `frames` / `principles` are *not* separate
 * API resources — they are type-scoped facades over the unified `/api/packages`
 * store (the worker only serves `/api/skills`, `/api/processes`, `/api/packages`,
 * `/api/package-links`). So every dataProvider call (`useShow` / `useUpdate` /
 * `useDelete`) targets the `packages` resource, while navigation uses the
 * facade's literal route base (`/bets/:id/edit`, …). The unified `packages`
 * route passes neither override and behaves as a plain Refine resource.
 *
 * Loading → skeleton; error / wrong-type → a calm inline Retry (never a crash).
 */
import * as React from "react";
import {
  useShow,
  useList,
  useGo,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { useParams } from "react-router-dom";
import { Pencil, Trash2 } from "@/components/ui/icon";

import type { Package, PackageLink, PackageType } from "@aos/shared";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/app/MarkdownView";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  Field,
  FieldGrid,
  RefLink,
  RelationsBlock,
  AuditBlock,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
} from "@/components/inspector";
import { formatDateTime } from "@/lib/format";
import { humanize } from "@/resources/projects/_form";

import {
  CompileAction,
  AdvanceMaturityAction,
  buildPackageRelations,
  packageMenuActions,
} from "./_actions";

export interface PackageInspectorShowProps {
  /**
   * The route base this show page lives under (e.g. `"packages"`, `"bets"`).
   * Drives Edit/Delete navigation (`/${resource}/:id/edit`) and the not-found
   * label fallback.
   */
  resource: string;
  /**
   * The Refine resource the dataProvider should target. Facades pass
   * `"packages"` (the real API path); the unified route leaves it unset and
   * defaults to `resource`.
   */
  dataResource?: string;
  /**
   * When set, the loaded record is guarded to this `package_type` — a facade
   * route (`/bets/:id`) never renders a record of a different type that happens
   * to share the id space.
   */
  expectType?: PackageType;
  /** Human label for the not-found message (e.g. "Bet"). Defaults to "Package". */
  label?: string;
  /** One-line header subtitle (feedback #6 — consistent header across pages). */
  subtitle?: string;
  /** Extra Header actions rendered left of the primary action (e.g. open graph). */
  extraActions?: (pkg: Package) => React.ReactNode;
  /** Override the single filled primary action (defaults to compile). */
  primaryAction?: (pkg: Package, refetch: () => void) => React.ReactNode;
}

export function PackageInspectorShow({
  resource,
  dataResource = resource,
  expectType,
  label = "Package",
  subtitle,
  extraActions,
  primaryAction,
}: PackageInspectorShowProps) {
  const go = useGo();
  const { id: routeId } = useParams<{ id: string }>();
  const { mutate: deletePackage } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState(false);

  const { query } = useShow<Package, HttpError>({
    resource: dataResource,
    id: routeId,
  });
  const { data, isLoading, isError, error, refetch } = query;
  const pkg = data?.data;
  const refresh = React.useCallback(() => {
    void refetch();
  }, [refetch]);

  // Package links: this package as source (outgoing) and as target (incoming).
  const { data: outgoing } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: pkg
      ? [{ field: "source_package_id", operator: "eq", value: pkg.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(pkg) },
  });
  const { data: incoming } = useList<PackageLink, HttpError>({
    resource: "package-links",
    filters: pkg
      ? [{ field: "target_package_id", operator: "eq", value: pkg.id }]
      : [],
    pagination: { pageSize: 200, mode: "server" },
    queryOptions: { enabled: Boolean(pkg) },
  });

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !pkg) {
    return (
      <ShowPageError
        message={error?.message ?? `${label} not found.`}
        onRetry={refresh}
      />
    );
  }
  if (expectType && pkg.package_type !== expectType) {
    return <ShowPageError message={`${label} not found.`} />;
  }

  const isSkill = pkg.package_type === "skill";
  const isContour = pkg.package_type === "contour";

  const relations = buildPackageRelations(
    pkg,
    outgoing?.data ?? [],
    incoming?.data ?? [],
  );

  const menuActions = packageMenuActions({
    onEdit: () => go({ to: `/${resource}/${pkg.id}/edit` }),
    onDelete: () => setPendingDelete(true),
    editIcon: Pencil,
    deleteIcon: Trash2,
  });

  return (
    <>
      <Inspector>
        <InspectorHeader
          title={pkg.name}
          subtitle={
            subtitle ??
            `A ${humanize(pkg.package_type).toLowerCase()} package — its maturity, contents, and typed links.`
          }
          status={pkg.status}
          actions={
            <>
              {extraActions?.(pkg)}
              <AdvanceMaturityAction pkg={pkg} onDone={refresh} />
            </>
          }
          primaryAction={
            primaryAction ? (
              primaryAction(pkg, refresh)
            ) : (
              <CompileAction packageId={pkg.id} />
            )
          }
          menuActions={menuActions}
        />

        <InspectorSection title="Overview">
          <FieldGrid>
            {pkg.description ? (
              <Field label="Description" full>
                <Markdown source={pkg.description} />
              </Field>
            ) : null}

            <Field label="Package type">
              <Badge variant="outline">{humanize(pkg.package_type)}</Badge>
            </Field>
            <Field label="Target level">{humanize(pkg.target_level)}</Field>
            <Field label="Maturity stage">
              {humanize(pkg.maturity_stage)}
            </Field>
            <Field label="Version">{pkg.version}</Field>
            <Field label="Domain">{pkg.domain}</Field>
            <Field label="Department">{pkg.department}</Field>
            <Field label="Owner">
              {pkg.owner_user_id ? (
                <RefLink resource="users" id={pkg.owner_user_id} />
              ) : null}
            </Field>
            <Field label="Created">{formatDateTime(pkg.created_at)}</Field>
            <Field label="Updated">{formatDateTime(pkg.updated_at)}</Field>

            {isSkill ? (
              <>
                <Field label="Short summary" full>
                  {pkg.short_summary}
                </Field>
                <Field label="Allowed executor">
                  {pkg.allowed_executor_type
                    ? humanize(pkg.allowed_executor_type)
                    : null}
                </Field>
                <Field label="Input contract" full>
                  {pkg.input_contract}
                </Field>
                <Field label="Output contract" full>
                  {pkg.output_contract}
                </Field>
              </>
            ) : null}

            {isContour ? (
              <>
                <Field label="Object type">
                  {pkg.object_type ? humanize(pkg.object_type) : null}
                </Field>
                <Field label="Rigidity level">{pkg.rigidity_level}</Field>
                <Field label="Transition mode">
                  {pkg.auto_transition === 1
                    ? "Strong (auto)"
                    : "Weak (scored)"}
                </Field>
                <Field label="Unknown transition">
                  {humanize(pkg.unknown_transition)}
                </Field>
                <Field label="Modes" full>
                  {pkg.modes}
                </Field>
              </>
            ) : null}
          </FieldGrid>
        </InspectorSection>

        {pkg.body_markdown ? (
          <InspectorSection title="Body">
            <Markdown source={pkg.body_markdown} />
          </InspectorSection>
        ) : null}

        <InspectorSection title="Relations">
          <RelationsBlock groups={relations} />
        </InspectorSection>

        <InspectorSection title="Technical">
          <FieldGrid className="text-muted-foreground">
            <Field label="Id">
              <TechnicalId id={pkg.id} />
            </Field>
            <Field label="Source path">{pkg.source_path}</Field>
          </FieldGrid>
        </InspectorSection>

        <InspectorSection title="Audit">
          <AuditBlock entityType="packages" entityId={pkg.id} />
        </InspectorSection>
      </Inspector>

      <ConfirmDialog
        open={pendingDelete}
        onOpenChange={setPendingDelete}
        title={`Delete ${label.toLowerCase()}?`}
        description={
          <>
            This removes <span className="font-medium">{pkg.name}</span> from
            the package store. This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() =>
          new Promise<void>((resolve) => {
            deletePackage(
              { resource: dataResource, id: pkg.id },
              {
                onSuccess: () => {
                  go({ to: `/${resource}` });
                  resolve();
                },
                onError: () => resolve(),
              },
            );
          })
        }
      />
    </>
  );
}

export default PackageInspectorShow;
